import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Reveal from "@/components/Reveal";

const BASE =
  "https://customer-assets-lqy194kg.emergentagent.net/job_contact-connect-59/artifacts/";

const PROJECTS = [
  {
    label: "Gris y blanco · Respaldo de madera",
    note: "Cocina en L con cubierta clara, campana integrada y respaldo tipo madera. Gavetas y canastillas extraíbles para aprovechar cada rincón.",
    photos: [
      "r18sjx3j_IMG_1806.jpeg",
      "6xtsutrm_IMG_1807.jpeg",
      "a8z47flv_IMG_1808.jpeg",
      "m2weg7t0_IMG_1809.jpeg",
    ],
  },
  {
    label: "Azul marino · Granito negro",
    note: "Muebles en azul profundo con cubierta de granito, fregadero doble de acero inoxidable y organizadores extraíbles.",
    photos: [
      "ny7upiu9_IMG_1816.jpeg",
      "sk1kkdko_IMG_1815.jpeg",
      "aeqat13h_IMG_1812.jpeg",
      "epqltoe3_IMG_1814.jpeg",
      "zqwtcuyo_IMG_1813.jpeg",
    ],
  },
  {
    label: "Blanco brillo · Madera natural",
    note: "Puertas en blanco brillo con muebles bajos de madera, horno y parrilla integrados, y una zona de desayuno junto al comedor.",
    photos: ["2rmr436f_IMG_1817.jpeg"],
  },
  {
    label: "Isla central · Blancos y grises",
    note: "Isla con barra para desayunar, iluminación bajo gabinete, microondas empotrado y puertas en gris brillo.",
    photos: ["gxhrts5w_IMG_1818.jpeg"],
  },
  {
    label: "Closet a medida · Nogal oscuro",
    note: "Closet fabricado a la medida del cuarto: gavetas interiores, barra de colgado y repisas en color claro.",
    photos: ["o53xalwu_IMG_1811.jpeg"],
  },
  {
    label: "Nogal oscuro · Cubierta clara",
    note: "Cocina en nogal con gavetas de apertura suave y cubierta clara: cualquier tipo de cocina a tu medida.",
    photos: ["/banner-kitchen.jpg"],
  },
];

function ParallaxPhoto({ src, alt, testId }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-28, 28]);

  return (
    <div ref={ref} className="group aspect-[16/10] overflow-hidden rounded-3xl">
      <motion.img
        src={src}
        alt={alt}
        style={{ y }}
        className="h-full w-full scale-[1.15] object-cover transition-transform duration-700 group-hover:scale-[1.22]"
        loading="lazy"
        data-testid={testId}
      />
    </div>
  );
}

function ProjectBlock({ project, index }) {
  const [main, ...thumbs] = project.photos;
  const num = String(index + 1).padStart(2, "0");
  const resolve = (p) => (p.startsWith("/") ? p : BASE + p);

  return (
    <Reveal y={36}>
      <article
        className="grid grid-cols-1 items-end gap-8 lg:grid-cols-12"
        data-testid={`portfolio-project-${num}`}
      >
        <div className={index % 2 === 1 ? "lg:order-2 lg:col-span-8" : "lg:col-span-8"}>
          <ParallaxPhoto
            src={resolve(main)}
            alt={`Proyecto Toscana — ${project.label}`}
            testId="portfolio-main-image"
          />
          {thumbs.length > 0 && (
            <div
              className="mt-4 grid gap-4"
              style={{
                gridTemplateColumns: `repeat(${Math.min(thumbs.length, 4)}, minmax(0, 1fr))`,
              }}
            >
              {thumbs.slice(0, 4).map((photo) => (
                <div
                  key={photo}
                  className="group aspect-[4/3] overflow-hidden rounded-2xl"
                  data-testid="portfolio-thumb"
                >
                  <img
                    src={BASE + photo}
                    alt={`Detalle — ${project.label}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          className={`lg:col-span-4 ${
            index % 2 === 1 ? "lg:order-1 lg:pr-8" : "lg:pl-8"
          }`}
        >
          <span className="text-sm tracking-[0.3em] text-cream/40">{num}</span>
          <h3 className="mt-4 font-serif text-3xl leading-tight text-cream lg:text-4xl">
            {project.label}
          </h3>
          <p className="mt-4 leading-relaxed text-cream/60">{project.note}</p>
          <div className="mt-8 h-px w-full bg-cream/10" />
        </div>
      </article>
    </Reveal>
  );
}

export default function Portfolio() {
  return (
    <section
      id="portafolio"
      className="bg-olive py-24 lg:py-32"
      data-testid="portfolio-section"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-12">
        <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <p className="eyebrow">Portafolio</p>
            <h2 className="mt-5 max-w-xl font-serif text-4xl leading-tight tracking-tight text-cream sm:text-5xl lg:text-6xl">
              Proyectos que hablan por sí solos.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:max-w-sm">
            <p className="leading-relaxed text-cream/60">
              Cada cocina nace de una casa distinta, de una familia distinta.
              Estas son algunas que ya están viviendo su mejor momento.
            </p>
          </Reveal>
        </div>

        <div className="mt-20 space-y-24 lg:space-y-32">
          {PROJECTS.map((project, i) => (
            <ProjectBlock key={project.label} project={project} index={i} />
          ))}
        </div>

        <Reveal y={36}>
          <div
            className="mt-24 grid items-center gap-10 border-t border-cream/10 pt-20 lg:grid-cols-12"
            data-testid="portfolio-video-block"
          >
            <div className="lg:col-span-7">
              <video
                className="aspect-video w-full rounded-3xl bg-black object-cover"
                src="/cliente-video.mp4"
                controls
                playsInline
                preload="metadata"
                data-testid="portfolio-video"
              />
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-cream/40">
                Cliente satisfecho · Proyecto entregado
              </p>
            </div>
            <div className="lg:col-span-5 lg:pl-6">
              <p className="eyebrow">Voces reales</p>
              <h3 className="mt-4 font-serif text-3xl leading-tight text-cream sm:text-4xl">
                Ellos ya viven su cocina.
              </h3>
              <p className="mt-4 leading-relaxed text-cream/60">
                Otro proyecto de cero a completa, con cliente satisfecho. No
                hay mejor carta de presentación que un cliente que estrenó su
                cocina con nosotros.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
